import React, { useState, useEffect } from "react";
import { X, Save, Loader2, Sparkles, Phone, Mail, Calendar, Heart, Briefcase, User } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import LeadDispositionBadge from "./LeadDispositionBadge";

/**
 * Slide-out drawer for editing a prospect's full relationship-intelligence profile.
 *
 * Layout: two sections — Personal + Business. Each field has an inline "AI-captured"
 * badge if the corresponding key is present in `captured_by_ai_fields`. The badge
 * disappears the moment a human edits that field (auto-cleared by the backend).
 */
const PERSONAL_FIELDS = [
  { key: "preferred_name",                 label: "Preferred name",     placeholder: "What they like to be called" },
  { key: "spouse_or_partner_name",         label: "Spouse / partner",   placeholder: "e.g. Jamie" },
  { key: "children",                       label: "Children",           placeholder: "Comma-separated names",  list: true },
  { key: "birthday",                       label: "Birthday",           placeholder: "YYYY-MM-DD or MM-DD",  type: "date" },
  { key: "anniversary_date",               label: "Anniversary",        placeholder: "YYYY-MM-DD",            type: "date" },
  { key: "pet_names",                      label: "Pet names",          placeholder: "Comma-separated names",  list: true },
  { key: "personal_interests",             label: "Personal interests", placeholder: "Hobbies, sports, hot topics", multi: true },
  { key: "preferred_communication_method", label: "Preferred contact",  placeholder: "phone | sms | email | in-person" },
];

const BUSINESS_FIELDS = [
  { key: "referral_source",            label: "Referral source",         placeholder: "Who referred them?" },
  { key: "lifetime_value_estimate",    label: "LTV estimate ($)",        placeholder: "0",  type: "number" },
  { key: "last_contact_date",          label: "Last contact",            placeholder: "YYYY-MM-DD",  type: "date" },
  { key: "next_followup_date",         label: "Next follow-up",          placeholder: "YYYY-MM-DD",  type: "date" },
  { key: "preferred_appointment_time", label: "Preferred appt. time",    placeholder: "e.g. weekday mornings" },
  { key: "service_history_summary",    label: "Service history",         placeholder: "Past jobs, repairs, installs", multi: true },
  { key: "open_issues_or_flags",       label: "Open issues / flags",     placeholder: "Anything sales / service should know", multi: true },
];

const toListString = (v) => (Array.isArray(v) ? v.join(", ") : v ?? "");
const fromListString = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

export default function ProspectDetailDrawer({ prospectId, onClose, onUpdate }) {
  const [prospect, setProspect] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!prospectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/prospects/${prospectId}`);
        if (cancelled) return;
        setProspect(data);
        setForm({
          ...data,
          children: toListString(data.children),
          pet_names: toListString(data.pet_names),
        });
      } catch (e) {
        toast.error("Could not load contact");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  const aiFields = prospect?.captured_by_ai_fields || {};

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        notes: form.notes ?? null,
        preferred_name: form.preferred_name ?? null,
        spouse_or_partner_name: form.spouse_or_partner_name ?? null,
        children: fromListString(form.children),
        birthday: form.birthday ?? null,
        anniversary_date: form.anniversary_date ?? null,
        pet_names: fromListString(form.pet_names),
        personal_interests: form.personal_interests ?? null,
        preferred_communication_method: form.preferred_communication_method ?? null,
        referral_source: form.referral_source ?? null,
        lifetime_value_estimate:
          form.lifetime_value_estimate === "" || form.lifetime_value_estimate == null
            ? null
            : Number(form.lifetime_value_estimate),
        last_contact_date: form.last_contact_date ?? null,
        next_followup_date: form.next_followup_date ?? null,
        next_followup_reminder: !!form.next_followup_reminder,
        preferred_appointment_time: form.preferred_appointment_time ?? null,
        service_history_summary: form.service_history_summary ?? null,
        open_issues_or_flags: form.open_issues_or_flags ?? null,
        relationship_health_score:
          form.relationship_health_score === "" || form.relationship_health_score == null
            ? null
            : Number(form.relationship_health_score),
      };
      const { data } = await api.patch(`/prospects/${prospectId}`, payload);
      toast.success("Saved");
      setProspect(data);
      onUpdate?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="prospect-detail-drawer"
      className="fixed inset-0 z-50 flex"
      onClick={onClose}
    >
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <div
        className="w-full max-w-xl h-full bg-[#0a0a0b] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0b]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Contact</div>
            <div className="text-lg font-medium text-white truncate" data-testid="drawer-prospect-name">
              {prospect?.name || "—"}
            </div>
            <div className="text-xs font-mono text-white/50 mt-0.5 flex items-center gap-3 flex-wrap">
              {prospect?.phone_normalized && (
                <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{prospect.phone_normalized}</span>
              )}
              {prospect?.email && (
                <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{prospect.email}</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close-btn"
            className="text-white/40 hover:text-white p-2 -m-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !prospect ? (
          <div className="p-12 text-center text-white/40 text-sm">Loading…</div>
        ) : (
          <div className="p-6 space-y-8">
            {/* Disposition row */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs font-mono uppercase tracking-widest text-white/40">Lead status</div>
              <LeadDispositionBadge
                prospectId={prospect.id}
                value={prospect.disposition}
                onUpdate={(p) => {
                  setProspect(p);
                  onUpdate?.(p);
                }}
              />
            </div>

            {/* Health score */}
            <SectionLabel icon={<Heart className="w-3.5 h-3.5" />} title="Relationship health" />
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`health-score-${n}`}
                  onClick={() => setField("relationship_health_score", n === Number(form.relationship_health_score) ? null : n)}
                  className={`w-9 h-9 rounded-md border text-xs font-mono transition-colors
                    ${Number(form.relationship_health_score) >= n
                      ? "bg-[#FFB000] border-[#FFB000] text-black"
                      : "bg-white/5 border-white/10 text-white/40 hover:border-white/20"}`}
                >
                  {n}
                </button>
              ))}
              <span className="text-[10px] font-mono uppercase tracking-widest text-white/30 ml-2">
                1 = needs work · 5 = champion
              </span>
            </div>

            {/* Personal */}
            <SectionLabel icon={<User className="w-3.5 h-3.5" />} title="Personal" />
            <div className="grid grid-cols-1 gap-4">
              {PERSONAL_FIELDS.map((f) => (
                <FieldInput key={f.key} field={f} value={form[f.key]} onChange={(v) => setField(f.key, v)} aiFlag={aiFields[f.key]} />
              ))}
            </div>

            {/* Business */}
            <SectionLabel icon={<Briefcase className="w-3.5 h-3.5" />} title="Business" />
            <div className="grid grid-cols-1 gap-4">
              {BUSINESS_FIELDS.map((f) => (
                <FieldInput key={f.key} field={f} value={form[f.key]} onChange={(v) => setField(f.key, v)} aiFlag={aiFields[f.key]} />
              ))}
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white/60">
                <input
                  type="checkbox"
                  checked={!!form.next_followup_reminder}
                  onChange={(e) => setField("next_followup_reminder", e.target.checked)}
                  className="accent-[#FFB000]"
                  data-testid="next-followup-reminder"
                />
                Send follow-up reminder
              </label>
            </div>

            {/* Notes */}
            <SectionLabel icon={<Calendar className="w-3.5 h-3.5" />} title="Notes" />
            <textarea
              data-testid="prospect-notes"
              value={form.notes || ""}
              onChange={(e) => setField("notes", e.target.value)}
              rows={4}
              className="w-full bg-[#121214] border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 placeholder:text-white/30 focus:border-[#FFB000]/50 focus:outline-none"
              placeholder="Anything else relevant…"
            />

            {/* Save */}
            <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-[#0a0a0b]/95 backdrop-blur border-t border-white/10 flex items-center gap-3">
              <button
                onClick={onSave}
                disabled={saving}
                data-testid="drawer-save-btn"
                className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-4 py-2 rounded-md transition-colors shadow-[0_0_18px_rgba(255,176,0,0.18)] disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save changes
              </button>
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white text-sm px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ icon, title }) {
  return (
    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/40 pt-1">
      {icon}
      {title}
    </div>
  );
}

function FieldInput({ field, value, onChange, aiFlag }) {
  const showAi = !!aiFlag;
  const inputCls =
    "w-full bg-[#121214] border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 placeholder:text-white/30 focus:border-[#FFB000]/50 focus:outline-none";
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">
          {field.label}
        </label>
        {showAi && (
          <span
            data-testid={`ai-captured-${field.key}`}
            className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[#FFB000] bg-[#FFB000]/10 ring-1 ring-[#FFB000]/30 px-1.5 py-0.5 rounded"
            title="Captured by AI"
          >
            <Sparkles className="w-2.5 h-2.5" />
            AI-captured
          </span>
        )}
      </div>
      {field.multi ? (
        <textarea
          data-testid={`field-${field.key}`}
          rows={2}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      ) : (
        <input
          data-testid={`field-${field.key}`}
          type={field.type || "text"}
          value={value ?? ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )}
    </div>
  );
}
